import { useEffect } from 'react';
import { Accordion, Box, Heading, Icon, Paragraph, Text } from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';
import type { BackgroundReport } from '@zg-rentals/ts-capps-client';
import { BackgroundReportAlert, getBackgroundReportAlerts, getFirstReportAlert } from './BackgroundReportAlerts';
import { getCurrencyDisplay, pluralize } from '@zg-rentals/applications-utils';
import { getTitleCase } from '../../../utils/formatters';
import { EvictionSectionContent } from './EvictionSectionContent';
import type { UserType } from '../../../models/report-models';
import { type EmitMetric, ReportType } from '../../../models/report-models';
import { countReportData } from '../../../utils/report-utils';

export function BackgroundReportEvictions({
  userType,
  backgroundReport,
  emitMetric,
}: {
  userType: UserType;
  backgroundReport: BackgroundReport;
  emitMetric: EmitMetric;
}) {
  const evictionRecordsCount = backgroundReport?.evictionReport?.records?.length || 0;

  useEffect(() => {
    countReportData(ReportType.BackgroundReport, 'evictions', evictionRecordsCount, emitMetric);
  }, []);

  const alert = getFirstReportAlert(getBackgroundReportAlerts(userType, backgroundReport), ['evictionReport'])?.summary;

  return (
    <>
      <Heading level={3} textStyle="heading-xs" css={{ marginTop: 'loose', marginBottom: 'loose' }}>
        Housing Court Record
      </Heading>
      <Box css={{ marginBottom: 'loose' }}>
        <BackgroundReportAlert
          userType={userType}
          backgroundReport={backgroundReport}
          onlySources={['evictionReport']}
        />
      </Box>

      <Paragraph>
        <Icon>
          <IconSearchFilled />
        </Icon>{' '}
        {alert ||
          (evictionRecordsCount > 0 ? (
            <>
              Total <strong>{pluralize('housing court record', evictionRecordsCount)}</strong> found.
            </>
          ) : (
            'No housing court records found.'
          ))}
      </Paragraph>

      <Paragraph textStyle="body-xs" css={{ marginTop: 'tighter', marginBottom: 'tighter' }}>
        The result of housing court record searches may vary according to (1) collection restrictions by the different
        court jurisdictions which may result in some areas not having coverage and (2) reporting restrictions due to
        government regulations applicable to the rental property’s location. CIC applies industry best practices in the
        attempt to accurately match and report these records.
      </Paragraph>
      {evictionRecordsCount > 0 && (
        <Accordion.Root>
          {backgroundReport?.evictionReport?.records?.map((record, index) => (
            <Accordion.Item key={index} value={`${index}`}>
              <Accordion.Header>
                <Heading level={4} css={{ textStyle: 'body' }}>
                  {getTitleCase(record.court)}
                </Heading>
                {record.judgementAmount && (
                  <Text textStyle="body-lg-bold">{getCurrencyDisplay(record.judgementAmount)}</Text>
                )}
              </Accordion.Header>
              <Accordion.Panel>
                <EvictionSectionContent record={record} />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion.Root>
      )}
    </>
  );
}
